import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { CareerRoleFullPictureStore } from '../shared/career-role-full-picture.store';
import { EditProfileSkillDependenciesDialogComponent } from '../shared/edit-profile-skill-dependencies-dialog.component';
import { EditProfileTopicDialogComponent } from '../shared/edit-profile-topic-dialog.component';
import {
  collectTopicOptionsFromProfile,
  getProfileSkills
} from '../shared/profile-skill-copy.util';
import {
  RoleDependencyGraphNode,
  RoleDependencyGraphSection,
  buildRoleDependencyGraphSections
} from './role-dependency-graph.util';

@Component({
  selector: 'app-career-role-graph-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatProgressBarModule,
    MatSelectModule,
    MatSnackBarModule
  ],
  templateUrl: './career-role-graph-tab.component.html',
  styleUrl: './career-role-graph-tab.component.scss'
})
export class CareerRoleGraphTabComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly pictureStore = inject(CareerRoleFullPictureStore);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);

  private readonly slugSignal = signal('');
  readonly roleState = computed(() => {
    const slug = this.slugSignal();
    if (!slug) {
      return null;
    }
    return this.pictureStore.getState(slug) ?? null;
  });

  readonly selectedSpecialization = signal('all');
  readonly selectedSeniority = signal('all');

  readonly sections = computed(() => buildRoleDependencyGraphSections(this.picture));
  readonly filteredSections = computed(() =>
    this.sections().filter((section) => {
      const specializationMatch =
        this.selectedSpecialization() === 'all' ||
        (this.selectedSpecialization() === 'base' && section.specializationSlug === null) ||
        this.selectedSpecialization() === section.specializationSlug;
      const seniorityMatch =
        this.selectedSeniority() === 'all' || this.selectedSeniority() === section.seniorityLevel;
      return specializationMatch && seniorityMatch;
    })
  );

  readonly specializationOptions = computed(() => {
    const options = [
      { value: 'all', label: 'All specializations' },
      { value: 'base', label: 'Base role only' }
    ];
    const seen = new Set<string>(['all', 'base']);
    for (const section of this.sections()) {
      if (section.specializationSlug && !seen.has(section.specializationSlug)) {
        seen.add(section.specializationSlug);
        options.push({
          value: section.specializationSlug,
          label: section.specializationName
        });
      }
    }
    return options;
  });

  readonly seniorityOptions = computed(() => {
    const options = [{ value: 'all', label: 'All seniority levels' }];
    const seen = new Set<string>(['all']);
    for (const section of this.sections()) {
      if (!seen.has(section.seniorityLevel)) {
        seen.add(section.seniorityLevel);
        options.push({
          value: section.seniorityLevel,
          label: this.formatLevel(section.seniorityLevel)
        });
      }
    }
    return options;
  });

  get slug(): string {
    return this.slugSignal();
  }

  get picture() {
    return this.roleState()?.picture ?? null;
  }

  get loading(): boolean {
    return this.roleState()?.loading ?? false;
  }

  get refreshing(): boolean {
    return this.roleState()?.refreshing ?? false;
  }

  get error(): string {
    return this.roleState()?.error ?? '';
  }

  ngOnInit(): void {
    const slug = this.route.parent?.snapshot.paramMap.get('slug') ?? '';
    this.slugSignal.set(slug);
    void this.load();
  }

  async load(force = false): Promise<void> {
    if (!this.slug) {
      return;
    }
    await this.pictureStore.load(this.slug, {
      force,
      background: !force && !!this.picture
    });
  }

  openEditNode(node: RoleDependencyGraphNode, section: RoleDependencyGraphSection): void {
    if (node.kind === 'skill') {
      this.openEditSkillDependencies(node, section);
      return;
    }
    this.openEditTopicDependencies(node, section);
  }

  private openEditSkillDependencies(
    node: RoleDependencyGraphNode,
    section: RoleDependencyGraphSection
  ): void {
    if (!this.picture) {
      return;
    }
    const specializationSlug = section.specializationSlug ?? undefined;
    const skills = getProfileSkills(this.picture, section.seniorityLevel, specializationSlug);
    const skill = skills.find((s) => s.skill.id === node.skillId);
    if (!skill) {
      this.snackBar.open('Skill was not found on this profile.', 'Close', { duration: 3000 });
      return;
    }
    const ref = this.dialog.open(EditProfileSkillDependenciesDialogComponent, {
      width: '520px',
      data: {
        roleSlug: this.slug,
        level: section.seniorityLevel,
        skillId: skill.skill.id,
        skillName: skill.skill.name,
        specializationSlug,
        dependsOn: skill.dependsOn ?? [],
        availableSkills: skills.map((s) => ({ id: s.skill.id, name: s.skill.name }))
      }
    });
    ref.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      this.snackBar.open(`Updated dependencies for ${result.skillName}`, 'Close', { duration: 3000 });
      this.pictureStore.invalidate(this.slug, { refresh: true });
    });
  }

  private openEditTopicDependencies(
    node: RoleDependencyGraphNode,
    section: RoleDependencyGraphSection
  ): void {
    if (!this.picture || !node.topic) {
      return;
    }
    const specializationSlug = section.specializationSlug ?? undefined;
    const skills = getProfileSkills(this.picture, section.seniorityLevel, specializationSlug);
    const ref = this.dialog.open(EditProfileTopicDialogComponent, {
      width: '520px',
      data: {
        roleSlug: this.slug,
        level: section.seniorityLevel,
        skillId: node.skillId,
        skillName: node.skillName,
        specializationSlug,
        topic: node.topic,
        availableTopics: collectTopicOptionsFromProfile(skills)
      }
    });
    ref.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      this.snackBar.open(`Updated topic on ${result.skillName}`, 'Close', { duration: 3000 });
      this.pictureStore.invalidate(this.slug, { refresh: true });
    });
  }

  totalRoots(section: RoleDependencyGraphSection): number {
    return section.skillRoots.length + section.topicRoots.length;
  }

  hasGraph(section: RoleDependencyGraphSection): boolean {
    return section.skillRoots.length > 0 || section.topicRoots.length > 0;
  }

  trackBySection(_index: number, section: RoleDependencyGraphSection): string {
    return section.key;
  }

  trackByNode(_index: number, node: RoleDependencyGraphNode): string {
    return node.id;
  }

  formatLevel(value: string): string {
    return value
      .split(/[^a-zA-Z0-9]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }
}